import { useMemo, useState } from "react";
import { formatDateRu, formatTimeRu, toDateInputValue } from "../../../utils/appointmentLabels";
import { sessionKindsOnDay } from "../../../utils/clientScheduleUtils";
import { groupName } from "../../../utils/clientSessionUtils";
import {
  monthGridDays as adminMonthGridDays,
  sessionColor,
  sessionLabel,
} from "../AdminSchedule/calendarUtils";
import DesktopDayTimeGridView from "../AdminSchedule/DesktopDayTimeGridView";
import DesktopMonthGridView from "../AdminSchedule/DesktopMonthGridView";
import md from "../common/manageDesktop.module.css";

const VIEWS = [
  { id: "day", label: "День" },
  { id: "month", label: "Месяц" },
];

function normalizeSessions(sessions) {
  return (sessions || []).map((sess) => ({
    ...sess,
    id: sess.session_id || sess.id,
  }));
}

function sessionsForDay(sessions, dayStr) {
  return sessions
    .filter((sess) => sess.start_datetime?.slice(0, 10) === dayStr)
    .sort(
      (a, b) =>
        new Date(a.start_datetime) - new Date(b.start_datetime),
    );
}

function SidebarList({ list, groups, onOpenSession }) {
  if (!list?.length) {
    return <p className={md.desktopRespCalendarSidebarEmpty}>Нет событий</p>;
  }
  return (
    <div className={md.desktopRespCalendarSidebarList}>
      {list.map((sess) => {
        const groupIdx = groups.findIndex((g) => g.id === sess.group?.id);
        const color = sessionColor(sess, groupIdx);
        return (
          <button
            key={sess.id}
            type="button"
            className={md.desktopRespCalendarSidebarListItem}
            onClick={() => onOpenSession?.(sess)}
          >
            <span
              className={md.desktopRespCalendarSidebarDot}
              style={{ background: color }}
              aria-hidden
            />
            <span className={md.desktopRespCalendarSidebarListLabel}>
              {sessionLabel(sess) || groupName(sess, groups)}
            </span>
            <span className={md.desktopRespCalendarSidebarListTime}>
              {formatTimeRu(sess.start_datetime)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ClientDesktopCalendar({
  anchorDate,
  viewMode,
  onViewMode,
  schedule,
  groups,
  loading,
  onSelectDay,
  onOpenSession,
  onShiftPeriod,
  onToday,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sessions = useMemo(() => normalizeSessions(schedule), [schedule]);
  const anchor = toDateInputValue(anchorDate);
  const monthDays = useMemo(() => adminMonthGridDays(anchor), [anchor]);
  const monthTitle = formatDateRu(anchorDate, { month: "long", year: "numeric" });
  const todayStr = toDateInputValue();
  const tomorrowStr = (() => {
    const d = new Date(`${todayStr}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return toDateInputValue(d);
  })();

  const todaySessions = useMemo(
    () => sessionsForDay(sessions, todayStr),
    [sessions, todayStr],
  );
  const tomorrowSessions = useMemo(
    () => sessionsForDay(sessions, tomorrowStr),
    [sessions, tomorrowStr],
  );

  const rangeLabel =
    viewMode === "month"
      ? monthTitle
      : formatDateRu(anchorDate, {
          weekday: "long",
          day: "numeric",
          month: "long",
        });

  const pickDay = (dayStr) => {
    onSelectDay(new Date(`${dayStr}T12:00:00`));
    if (viewMode === "month") onViewMode("day");
  };

  return (
    <div className={`${md.desktopCalendar} clientDesktopCalendar`}>
      <div
        className={[
          md.desktopRespCalendarSplit,
          !sidebarOpen ? md.desktopRespCalendarSplitCollapsed : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <aside className={md.desktopRespCalendarSidebar}>
          <h3 className={md.desktopRespCalendarSidebarTitle}>{monthTitle}</h3>

          <div className={md.desktopRespCalendarMini}>
            <div className={md.desktopRespCalendarMiniWeekdays}>
              {["пн", "вт", "ср", "чт", "пт", "сб", "вс"].map((w) => (
                <div key={w} className={md.desktopRespCalendarMiniWeekday}>
                  {w}
                </div>
              ))}
            </div>
            <div className={md.desktopRespCalendarMiniGrid}>
              {monthDays.map((day) => {
                const inMonth = day.slice(0, 7) === anchor.slice(0, 7);
                const selected = day === anchor;
                const today = day === todayStr;
                const dayKinds = sessionKindsOnDay(
                  sessions,
                  new Date(`${day}T12:00:00`),
                );
                const hasEvents =
                  dayKinds.training > 0 || dayKinds.competition > 0;
                return (
                  <button
                    key={day}
                    type="button"
                    className={[
                      md.desktopRespCalendarMiniCell,
                      !inMonth ? md.desktopRespCalendarMiniCellMuted : "",
                      today ? md.desktopRespCalendarMiniCellToday : "",
                      selected ? md.desktopRespCalendarMiniCellSelected : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => pickDay(day)}
                  >
                    <span className={md.desktopRespCalendarMiniDayNum}>
                      {Number(day.slice(8, 10))}
                    </span>
                    <span className={md.desktopRespCalendarMiniDots} aria-hidden>
                      {dayKinds.training > 0 ? (
                        <span
                          className={md.desktopRespCalendarMiniDot}
                          style={{ background: "var(--color-primary, #7c3aed)" }}
                        />
                      ) : null}
                      {dayKinds.competition > 0 ? (
                        <span
                          className={md.desktopRespCalendarMiniDot}
                          style={{ background: "#d97706" }}
                        />
                      ) : null}
                      {!hasEvents ? (
                        <span className={md.desktopRespCalendarMiniDotSpacer} />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={md.desktopRespCalendarUpcoming}>
            <div className={md.desktopRespCalendarSidebarSection}>
              <p className={md.desktopRespCalendarUpcomingTitle}>Сегодня</p>
              <SidebarList
                list={todaySessions}
                groups={groups}
                onOpenSession={onOpenSession}
              />
            </div>
            <div className={md.desktopRespCalendarSidebarSection}>
              <p className={md.desktopRespCalendarUpcomingTitle}>Завтра</p>
              <SidebarList
                list={tomorrowSessions}
                groups={groups}
                onOpenSession={onOpenSession}
              />
            </div>
          </div>
        </aside>

        <div className={md.desktopRespCalendarMain}>
          <div className={md.desktopRespCalendarHeader}>
            <div className={md.desktopRespCalendarHeaderLeft}>
              <button
                type="button"
                className={md.desktopRespCalendarSidebarToggleBtn}
                aria-label="Мини-календарь"
                onClick={() => setSidebarOpen((v) => !v)}
              >
                ☰
              </button>
              <div className={md.desktopRespCalendarNavGroup}>
                <button
                  type="button"
                  className={md.desktopRespCalendarNavBtn}
                  onClick={() => onShiftPeriod(-1)}
                  aria-label="Назад"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={md.desktopRespCalendarTodayBtn}
                  onClick={onToday}
                >
                  Сегодня
                </button>
                <button
                  type="button"
                  className={md.desktopRespCalendarNavBtn}
                  onClick={() => onShiftPeriod(1)}
                  aria-label="Вперёд"
                >
                  ›
                </button>
              </div>
              <h2 className={md.desktopRespCalendarHeaderTitle}>{rangeLabel}</h2>
            </div>
            <div className={md.desktopRespCalendarHeaderRight}>
              <div className={md.desktopRespCalendarViewSwitch}>
                {VIEWS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={
                      viewMode === item.id
                        ? md.desktopRespCalendarModeBtnActive
                        : md.desktopRespCalendarModeBtn
                    }
                    onClick={() => onViewMode(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={md.desktopRespCalendarBody}>
            {loading ? (
              <div className={md.desktopRespCalendarSidebarEmpty} style={{ padding: 24 }}>
                Загрузка…
              </div>
            ) : viewMode === "month" ? (
              <DesktopMonthGridView
                days={monthDays}
                anchor={anchor}
                sessions={sessions}
                groups={groups}
                onDayClick={pickDay}
                onSessionClick={(e, day, sess) => {
                  e.stopPropagation();
                  onSelectDay(new Date(`${day}T12:00:00`));
                  onOpenSession(sess);
                }}
              />
            ) : (
              <DesktopDayTimeGridView
                day={anchor}
                sessions={sessions}
                groups={groups}
                onOpenSession={onOpenSession}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
